import { Injectable } from '@angular/core';
import { DraasApiConfig } from './disaster-recovery-constants';
import { map, Observable } from 'rxjs';
import {
    DraasApiAWSProvider,
    DraasApiBlobData,
    DraasApiCloudFormationTemplate,
    DraasApiCluster,
    DraasApiConfigureVSphereStorage,
    DraasApiCreateDeployment,
    DraasApiCreateAWSProviderV2,
    DraasApiCreateVSphereProviderV2,
    DraasApiVSphereNetwork,
    DraasApiVSphereProvider,
    DraasApiUpdateAWSProviderNetwork,
    DraasApiOvaUrl,
    DraasApiUpdateAWSRecoveryNetworkConfig,
    DraasApiUpdateVSphereRecoveryStorageConfig,
    DraasApiUpdateVSphereRecoveryNetworkConfig,
    DraasApiDeploymentExtended,
    DraasApiUpdateDeploymentDetails,
} from '@pure/paas-api-gateway-client-ts';
import { DisasterRecoveryCluster } from '../models/disaster-recovery-cluster';
import { DisasterRecoveryAwsProvider, DisasterRecoveryVSphereProvider } from '../models/disaster-recovery-provider';
import { DisasterRecoveryThrottlingHttpClient } from './disaster-recovery-throttling-http-client.service';
import { DisasterRecoveryCloudFormationTemplate } from '../models/disaster-recovery-cloud-formation-template';
import { DisasterRecoveryDeploymentExtended } from '../models/disaster-recovery-deployment-extended';

@Injectable({ providedIn: 'root' })
export class DisasterRecoveryDeploymentsService {
    constructor(protected http: DisasterRecoveryThrottlingHttpClient) {}

    private getEndpoint(): string {
        return `${DraasApiConfig.getUrlPrefix()}/api/2.0/deployments`;
    }

    getDeploymentsExtended(pageNumber: number, pageSize: number): Observable<DisasterRecoveryDeploymentExtended[]> {
        const queryParams = [`page_number=${pageNumber}`, `page_size=${pageSize}`].join('&');
        const url = `${this.getEndpoint()}/extended?${queryParams}`;
        return this.http
            .get<DraasApiDeploymentExtended[]>(url)
            .pipe(
                map(deployments => deployments.map(deployment => new DisasterRecoveryDeploymentExtended(deployment))),
            );
    }

    createDeployment(name: string, licenseKey: string, description: string): Observable<DisasterRecoveryCluster> {
        const url = this.getEndpoint();
        const body: DraasApiCreateDeployment = {
            name,
            license_key: licenseKey,
            description,
        };
        return this.http.post<DraasApiCluster>(url, body).pipe(map(cluster => new DisasterRecoveryCluster(cluster)));
    }

    createVSphereSite(clusterId: string, name: string): Observable<DisasterRecoveryVSphereProvider> {
        const url = `${this.getEndpoint()}/${clusterId}/vsphere`;
        const body: DraasApiCreateVSphereProviderV2 = {
            name,
        };
        return this.http
            .post<DraasApiVSphereProvider>(url, body)
            .pipe(map(provider => new DisasterRecoveryVSphereProvider(provider)));
    }

    createAwsSite(clusterId: string, name: string): Observable<DisasterRecoveryAwsProvider> {
        const url = `${this.getEndpoint()}/${clusterId}/aws`;
        const body: DraasApiCreateAWSProviderV2 = {
            name,
        };
        return this.http
            .post<DraasApiAWSProvider>(url, body)
            .pipe(map(provider => new DisasterRecoveryAwsProvider(provider)));
    }

    configureVSphereWorkerVmNetwork(
        clusterId: string,
        providerId: string,
        body: DraasApiVSphereNetwork,
    ): Observable<DisasterRecoveryVSphereProvider> {
        const url = `${this.getEndpoint()}/${clusterId}/vsphere/${providerId}/config/worker-vm-network`;
        return this.http
            .post<DraasApiVSphereProvider>(url, body)
            .pipe(map(provider => new DisasterRecoveryVSphereProvider(provider)));
    }

    getVSphereBlob(clusterId: string, providerId: string): Observable<string> {
        const url = `${this.getEndpoint()}/${clusterId}/vsphere/${providerId}/blob`;
        return this.http.get<DraasApiBlobData>(url).pipe(map(blobData => blobData.blob));
    }

    getOvaUrl(clusterId: string): Observable<string> {
        const url = `${this.getEndpoint()}/${clusterId}/ova-url`;
        return this.http.post<DraasApiOvaUrl>(url, null).pipe(map(blobData => blobData.url));
    }

    configureVSphereStorage(
        clusterId: string,
        providerId: string,
        storage: DraasApiConfigureVSphereStorage,
    ): Observable<DisasterRecoveryVSphereProvider> {
        const url = `${this.getEndpoint()}/${clusterId}/vsphere/${providerId}/config/storage`;
        return this.http
            .post<DraasApiVSphereProvider>(url, storage)
            .pipe(map(provider => new DisasterRecoveryVSphereProvider(provider)));
    }

    configureAwsNetwork(
        clusterId: string,
        providerId: string,
        network: DraasApiUpdateAWSProviderNetwork,
    ): Observable<DisasterRecoveryAwsProvider> {
        const url = `${this.getEndpoint()}/${clusterId}/aws/${providerId}/config/network`;
        return this.http
            .post<DraasApiAWSProvider>(url, network)
            .pipe(map(provider => new DisasterRecoveryAwsProvider(provider)));
    }

    generateCloudFormation(clusterId: string, providerId: string): Observable<DisasterRecoveryCloudFormationTemplate> {
        const url = `${this.getEndpoint()}/${clusterId}/aws/${providerId}/generate-cloud-formation`;
        return this.http
            .post<DraasApiCloudFormationTemplate>(url, {})
            .pipe(map(cloudFormationTemplate => new DisasterRecoveryCloudFormationTemplate(cloudFormationTemplate)));
    }

    updateAwsRecoveryNetworkConfiguration(
        clusterId: string,
        providerId: string,
        config: DraasApiUpdateAWSRecoveryNetworkConfig,
    ): Observable<DisasterRecoveryAwsProvider> {
        const url = `${this.getEndpoint()}/${clusterId}/aws/${providerId}/config/recovery/network`;
        return this.http
            .patch<DraasApiAWSProvider>(url, config)
            .pipe(map(provider => new DisasterRecoveryAwsProvider(provider)));
    }

    updateVSphereRecoveryStorageConfiguration(
        clusterId: string,
        siteId: string,
        config: DraasApiUpdateVSphereRecoveryStorageConfig,
    ): Observable<DisasterRecoveryVSphereProvider> {
        const url = `${this.getEndpoint()}/${clusterId}/vsphere/${siteId}/config/recovery/storage`;
        return this.http
            .patch<DraasApiVSphereProvider>(url, config)
            .pipe(map(provider => new DisasterRecoveryVSphereProvider(provider)));
    }

    updateVSphereRecoveryNetworkConfiguration(
        clusterId: string,
        siteId: string,
        config: DraasApiUpdateVSphereRecoveryNetworkConfig,
    ): Observable<DisasterRecoveryVSphereProvider> {
        const url = `${this.getEndpoint()}/${clusterId}/vsphere/${siteId}/config/recovery/network`;
        return this.http
            .patch<DraasApiVSphereProvider>(url, config)
            .pipe(map(provider => new DisasterRecoveryVSphereProvider(provider)));
    }

    updateDetails(clusterId: string, details: DraasApiUpdateDeploymentDetails): Observable<void> {
        const url = `${this.getEndpoint()}/${clusterId}/details`;
        return this.http.patch<void>(url, details);
    }
}
